// WP-0.7 (§18.2 navigation map): the complete route table. Every screen in the spec has a
// stub from P0 so the shell never dead-ends; feature WPs replace the stub component.
// `nav` marks which routes appear in the sidebar and under which group (§18.1 order).

export type NavGroup =
  | 'top' // Home, Inbox
  | 'money'
  | 'work'
  | 'insight'
  | 'plan'
  | 'schedule'
  | 'bottom'; // Import, Settings

export interface RouteDef {
  path: string;
  screenId: string; // S-xx from §18.3
  title: string;
  public?: boolean; // reachable without a session
  standalone?: boolean; // no app chrome (setup/login/onboarding)
  nav?: { group: NavGroup; label: string; icon: string; order: number };
}

export const ROUTES: readonly RouteDef[] = [
  // standalone / auth
  { path: '/setup', screenId: 'S-01', title: 'Setup', public: true, standalone: true },
  { path: '/login', screenId: 'S-02', title: 'Login', public: true, standalone: true },
  { path: '/onboarding', screenId: 'S-04', title: 'Onboarding', standalone: true },

  // home + inbox (top group)
  {
    path: '/',
    screenId: 'S-05',
    title: 'Dashboard',
    nav: { group: 'top', label: 'Home', icon: 'LayoutDashboard', order: 0 },
  },
  {
    path: '/inbox',
    screenId: 'S-07',
    title: 'Inbox',
    nav: { group: 'top', label: 'Inbox', icon: 'Inbox', order: 1 },
  },
  { path: '/inbox/:id', screenId: 'S-08', title: 'Inbox item' },
  {
    path: '/chat',
    screenId: 'S-06',
    title: 'Assistant',
    nav: { group: 'top', label: 'Chat', icon: 'MessageCircle', order: 2 },
  },

  // money
  {
    path: '/tx',
    screenId: 'S-09',
    title: 'Transactions',
    nav: { group: 'money', label: 'Transactions', icon: 'ArrowLeftRight', order: 0 },
  },
  { path: '/tx/:id', screenId: 'S-10', title: 'Transaction' },
  {
    path: '/accounts',
    screenId: 'S-11',
    title: 'Accounts',
    nav: { group: 'money', label: 'Accounts', icon: 'Wallet', order: 1 },
  },
  { path: '/accounts/:id', screenId: 'S-12', title: 'Account' },
  { path: '/accounts/:id/reconcile', screenId: 'S-13', title: 'Reconcile' },
  {
    path: '/expected',
    screenId: 'S-14',
    title: 'Expected',
    nav: { group: 'money', label: 'Expected', icon: 'CalendarClock', order: 2 },
  },
  {
    path: '/owed',
    screenId: 'S-15',
    title: 'Owed',
    nav: { group: 'money', label: 'Owed (R&P)', icon: 'HandCoins', order: 3 },
  },
  {
    path: '/tpf',
    screenId: 'S-16',
    title: 'Third-party',
    nav: { group: 'money', label: 'Third-party', icon: 'Users', order: 4 },
  },
  { path: '/tpf/:id', screenId: 'S-17', title: 'Third-party case' },
  {
    path: '/recurring',
    screenId: 'S-18',
    title: 'Recurring',
    nav: { group: 'money', label: 'Recurring', icon: 'Repeat', order: 5 },
  },

  // work
  {
    path: '/parties',
    screenId: 'S-19',
    title: 'Parties',
    nav: { group: 'work', label: 'Parties', icon: 'Contact', order: 0 },
  },
  { path: '/parties/:id', screenId: 'S-20', title: 'Party' },
  {
    path: '/quotes',
    screenId: 'S-21',
    title: 'Quotations',
    nav: { group: 'work', label: 'Quotations', icon: 'FileText', order: 1 },
  },
  { path: '/quotes/:id', screenId: 'S-22', title: 'Quotation' },
  {
    path: '/projects',
    screenId: 'S-23',
    title: 'Projects',
    nav: { group: 'work', label: 'Projects', icon: 'Briefcase', order: 2 },
  },
  { path: '/projects/:id', screenId: 'S-24', title: 'Project' },

  // insight
  {
    path: '/intel',
    screenId: 'S-25',
    title: 'Intelligence',
    nav: { group: 'insight', label: 'Intelligence', icon: 'ChartColumn', order: 0 },
  },
  {
    path: '/explorer',
    screenId: 'S-26',
    title: 'Explorer',
    nav: { group: 'insight', label: 'Explorer', icon: 'Waypoints', order: 1 },
  },
  {
    path: '/reports',
    screenId: 'S-27',
    title: 'Reports',
    nav: { group: 'insight', label: 'Reports', icon: 'FileBarChart', order: 2 },
  },
  { path: '/reports/:key', screenId: 'S-28', title: 'Report' },
  { path: '/reports/position-over-time', screenId: 'S-28', title: 'Position over time' }, // WP-5.1 (RE-2) — a static path scores above the dynamic :key above, react-router picks this one
  {
    path: '/timeline',
    screenId: 'S-29',
    title: 'Timeline',
    nav: { group: 'insight', label: 'Timeline', icon: 'History', order: 3 },
  },
  {
    path: '/health',
    screenId: 'S-30',
    title: 'Data Health',
    nav: { group: 'insight', label: 'Data Health', icon: 'HeartPulse', order: 4 },
  },

  // plan
  {
    path: '/goals',
    screenId: 'S-31',
    title: 'Goals',
    nav: { group: 'plan', label: 'Goals', icon: 'Target', order: 0 },
  },
  {
    path: '/advisor',
    screenId: 'S-32',
    title: 'Advisor',
    nav: { group: 'plan', label: 'Advisor', icon: 'Lightbulb', order: 1 },
  },
  {
    path: '/simulator',
    screenId: 'S-33',
    title: 'Simulator',
    nav: { group: 'plan', label: 'Simulator', icon: 'FlaskConical', order: 2 },
  },

  // schedule
  {
    path: '/agenda',
    screenId: 'S-34',
    title: 'Agenda',
    nav: { group: 'schedule', label: 'Agenda', icon: 'Calendar', order: 0 },
  },
  {
    path: '/availability',
    screenId: 'S-35',
    title: 'Availability',
    nav: { group: 'schedule', label: 'Availability', icon: 'CalendarCheck', order: 1 },
  },
  {
    path: '/reminders',
    screenId: 'S-36',
    title: 'Reminders',
    nav: { group: 'schedule', label: 'Reminders', icon: 'BellRing', order: 2 },
  },

  // utility + bottom group
  { path: '/search', screenId: 'S-37', title: 'Search' },
  {
    path: '/import',
    screenId: 'S-38',
    title: 'Import',
    nav: { group: 'bottom', label: 'Import', icon: 'Upload', order: 0 },
  },
  {
    path: '/settings',
    screenId: 'S-39',
    title: 'Settings',
    nav: { group: 'bottom', label: 'Settings', icon: 'Settings', order: 1 },
  },
];

export const NAV_GROUP_LABELS: Record<Exclude<NavGroup, 'top' | 'bottom'>, string> = {
  money: 'Money',
  work: 'Work',
  insight: 'Insight',
  plan: 'Plan',
  schedule: 'Schedule',
};
